import { t } from "i18next";
import { CONST } from "models/constants";
import LabeledField from "providers/labeledField";
import {
  MdFileDownload,
  MdOutlineCheckCircle,
  MdOutlineComment,
  MdOutlineEscalator,
  MdOutlineStopCircle,
  MdViewAgenda,
} from "react-icons/md";
import GenericCard from "widgets/cards/generic";
import CircleButton from "widgets/controls/buttons/circle-button";
import { Alert } from "api/types/alerts";
import { formatDate } from "util/datelib";
import { useState } from "react";
import AlertsActionsDialog from "widgets/dialogs/alertsActions";
import AlertsConfigDialog from "widgets/dialogs/alertsConfig";
import { reportAlertDetails } from "api/report";
import { setLoading } from "store/appSlice";
import { useDispatch } from "react-redux";

interface GeneralProps {
  alert?: Alert;
  id: string;
  onUpdate?: () => void;
}

const General: React.FC<GeneralProps> = ({ alert, id, onUpdate }) => {
  const dispatch = useDispatch();
  const [showAlertConfig, setShowAlertConfig] = useState(false);
  const [showAlertsActions, setShowAlertsActions] = useState(false);
  const [alertContext, setAlertContext] = useState<
    "ESCALATE" | "ACKNOWLEDGED" | "CLOSED" | "COMMENT" | null
  >(null);

  const handleClose = (refresh: boolean) => {
    setShowAlertConfig(false);
    setShowAlertsActions(false);
    setAlertContext(null);
    if (refresh) {
      onUpdate();
    }
  };

  const handleShowAlertsActions = (
    context: "ESCALATE" | "ACKNOWLEDGED" | "CLOSED" | "COMMENT"
  ) => {
    setAlertContext(context);
    setShowAlertsActions(true);
  };

  // Check if escalation is disabled (alert has reached maximum level)
  const isEscalationDisabled = () => {
    if (!alert?.config?.levels || alert.config.levels.length === 0) {
      return false; // No escalation config, allow escalation
    }

    // Find the maximum level from config
    const maxLevel = Math.max(
      ...alert.config.levels.map((level) => level.index)
    );

    // Disable if current level equals or exceeds maximum level
    return alert.level >= maxLevel;
  };

  const downloadAlertDetails = async () => {
    dispatch(setLoading(true));
    const { data: res, isLoading, status } = await reportAlertDetails(id);
    dispatch(setLoading(isLoading));
    if (status === 200 && res.url && res.url.pdfUrl) {
      const link = document.createElement("a");
      link.href = res.url.pdfUrl;
      link.target = "_blank";
      link.download = "alert-details.pdf";
      link.click();
    }
  };

  return (
    <>
      {showAlertConfig && (
        <AlertsConfigDialog
          config={alert?.config.levels}
          isOpen={showAlertConfig}
          onClose={() => {
            handleClose(false);
          }}
        />
      )}
      {showAlertsActions && (
        <AlertsActionsDialog
          context={alertContext}
          alertId={id}
          isOpen={showAlertsActions}
          onClose={() => {
            handleClose(false);
          }}
          onSuccess={() => {
            handleClose(true);
          }}
        />
      )}
      <GenericCard
        title={t(`alerts.general.type`, { context: alert?.type.toLowerCase() })}
        bgColor="200"
        extraTitle="text-white heading-md h-fit"
        topRight={
          <div className="-mt-2 flex">
            <CircleButton
              icon={MdFileDownload}
              size="md"
              color="white"
              onClick={downloadAlertDetails}
              tooltip={t("tooltip.download.alert.details")}
              tooltipPlacement="bottom"
              variant="ghost"
              permissionRole={CONST.ROLE_OWNER}
            />
            <CircleButton
              icon={MdViewAgenda}
              size="md"
              color="white"
              onClick={() => {
                setShowAlertConfig(true);
              }}
              tooltip={t("tooltip.view.alert.configuration")}
              tooltipPlacement="bottom"
              variant="ghost"
              permissionRole={CONST.ROLE_OWNER}
            />
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("alerts.details.created")}
            id={"alerts-details-created"}
          >
            <div className="text-txt-lg text-white">
              {formatDate(alert?.created, "lastcomms") ||
                t("alerts.details.created")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("alerts.details.last_interaction")}
            id={"alerts-details-last-interaction"}
          >
            <div className="text-txt-lg text-white">
              {formatDate(alert?.lastInteractionDate, "lastcomms") ||
                t("alerts.details.last_interaction")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("alerts.details.level")}
            id={"alerts-details-level"}
          >
            <div className="text-txt-lg text-white">
              {alert?.level || t("alerts.details.level")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("alerts.details.state")}
            id={"alerts-details-state"}
          >
            <div className="text-txt-lg text-white">
              {alert?.state || t("alerts.details.state")}
            </div>
          </LabeledField>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <CircleButton
            label={t("alerts.actions.comment")}
            onClick={() => {
              handleShowAlertsActions("COMMENT");
            }}
            size="md"
            variant="solid"
            icon={MdOutlineComment}
            iconPosition="right"
            color="white"
            extra="rounded-lg justify-center"
          />
          <CircleButton
            label={t("alerts.actions.escalate")}
            onClick={() => {
              handleShowAlertsActions("ESCALATE");
            }}
            size="md"
            variant="solid"
            disabled={alert?.state === "CLOSED" || isEscalationDisabled()}
            icon={MdOutlineEscalator}
            iconPosition="right"
            color="white"
            extra="rounded-lg justify-center"
          />
          <CircleButton
            label={t("alerts.actions.acknowledge")}
            onClick={() => {
              handleShowAlertsActions("ACKNOWLEDGED");
            }}
            size="md"
            variant="solid"
            disabled={alert?.state === "CLOSED"}
            icon={MdOutlineCheckCircle}
            iconPosition="right"
            color="white"
            extra="rounded-lg justify-center"
          />
          <CircleButton
            label={t("alerts.actions.close")}
            onClick={() => {
              handleShowAlertsActions("CLOSED");
            }}
            size="md"
            variant="solid"
            disabled={alert?.state === "CLOSED"}
            icon={MdOutlineStopCircle}
            iconPosition="right"
            color="white"
            extra="rounded-lg justify-center"
          />
        </div>
      </GenericCard>
    </>
  );
};

export default General;
